import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, Star, Tv, Film, Trophy, Zap, Shield, Clock, Users, Play } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const IptvFrance = () => {
  useAffiliateTracking();
  const faqData = [
    {
      question: "Qu'est-ce que l'IPTV France ?",
      answer: "L'IPTV France est un service de télévision par internet qui permet d'accéder à toutes les chaînes françaises (TF1, France 2, M6, Canal+, beIN Sports, RMC Sport) ainsi qu'aux films, séries et événements sportifs en streaming haute qualité."
    },
    {
      question: "L'IPTV est-il légal en France ?",
      answer: "L'utilisation de services IPTV pour accéder à du contenu dont vous possédez les droits est légale. Notre service propose un accès premium avec une qualité de diffusion optimale et un support client réactif."
    },
    {
      question: "Quelles chaînes françaises sont disponibles ?",
      answer: "Toutes les chaînes TNT (TF1, France 2, France 3, M6, Arte, W9...), les chaînes Canal+ (Canal+ Sport, Cinema, Séries), beIN Sports, RMC Sport, Eurosport, et plus de 500 chaînes françaises parmi nos +15 000 chaînes TV."
    },
    {
      question: "Puis-je regarder la Ligue 1 et la Champions League ?",
      answer: "Oui ! Vous avez accès à tous les matchs de Ligue 1, Ligue 2, Champions League, Europa League, Premier League, Liga et Serie A en direct et en qualité 4K."
    },
    {
      question: "Quelle est la qualité de diffusion ?",
      answer: "Notre service propose une diffusion en 4K HDR, Full HD et HD selon votre connexion internet. La qualité s'adapte automatiquement pour une expérience optimale sans coupure."
    },
    {
      question: "Comment fonctionne le test gratuit 24h ?",
      answer: "Vous remplissez simplement le formulaire avec votre email, et vous recevez immédiatement vos identifiants pour tester l'intégralité du service pendant 24 heures, sans engagement et sans carte bancaire."
    },
    {
      question: "Sur quels appareils puis-je regarder ?",
      answer: "Compatible avec Smart TV Samsung/LG/Sony, smartphones Android et iPhone, tablettes, PC Windows et Mac, Amazon Fire TV Stick, box Android, Chromecast et Apple TV."
    },
    {
      question: "Le service fonctionne-t-il à l'étranger ?",
      answer: "Oui, vous pouvez accéder à toutes les chaînes françaises depuis n'importe quel pays. Idéal pour les expatriés français ou les voyageurs."
    }
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "IPTV France - Abonnement Streaming Premium",
    "description": "Service IPTV France avec accès à +15 000 chaînes TV, +100 000 films et +25 000 séries, sport en direct 4K. Test gratuit 24h.",
    "brand": { "@type": "Brand", "name": "RoiDuStreaming" },
    "offers": {
      "@type": "AggregateOffer",
      "offerCount": 3,
      "lowPrice": "25",
      "highPrice": "60",
      "priceCurrency": "EUR",
      "offers": [
        { "@type": "Offer", "name": "3 Mois", "price": "25", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "priceValidUntil": "2026-12-31", "url": "https://roidustreaming.fr/iptv-france",
          "hasMerchantReturnPolicy": { "@type": "MerchantReturnPolicy", "applicableCountry": "FR", "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 14, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/FreeReturn" },
          "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" }, "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "FR" }, "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "h" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "h" } } }
        },
        { "@type": "Offer", "name": "6 Mois", "price": "40", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "priceValidUntil": "2026-12-31", "url": "https://roidustreaming.fr/iptv-france",
          "hasMerchantReturnPolicy": { "@type": "MerchantReturnPolicy", "applicableCountry": "FR", "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 14, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/FreeReturn" },
          "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" }, "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "FR" }, "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "h" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "h" } } }
        },
        { "@type": "Offer", "name": "12 Mois", "price": "60", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "priceValidUntil": "2026-12-31", "url": "https://roidustreaming.fr/iptv-france",
          "hasMerchantReturnPolicy": { "@type": "MerchantReturnPolicy", "applicableCountry": "FR", "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 14, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/FreeReturn" },
          "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" }, "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "FR" }, "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "h" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "h" } } }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "2847"
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://roidustreaming.fr" },
      { "@type": "ListItem", "position": 2, "name": "IPTV France", "item": "https://roidustreaming.fr/iptv-france" }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>IPTV France 2025 - Meilleur Service Streaming | +15 000 Chaînes</title>
        <meta name="description" content="IPTV France : accédez à +15 000 chaînes TV, +100 000 films, +25 000 séries et sport en direct 4K. Test gratuit 24h. Abonnement dès 25€." />
        <meta name="keywords" content="iptv france, iptv français, chaînes françaises iptv, iptv canal+, iptv bein sport, streaming france" />
        <link rel="canonical" href="https://roidustreaming.fr/iptv-france" />
        <meta property="og:title" content="IPTV France 2025 - Meilleur Service Streaming Français" />
        <meta property="og:description" content="Accédez à +15 000 chaînes TV, films, séries et sport en 4K. Test gratuit 24h." />
        <meta property="og:url" content="https://roidustreaming.fr/iptv-france" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-b from-primary/10 to-background">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Star className="w-4 h-4 fill-primary" />
            <span>Service #1 en France - 4.8/5 (2847 avis)</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="gradient-text">IPTV France</span> - Le Meilleur Service
            <br />Streaming Français en 2025
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Accédez à <strong className="text-foreground">+15 000 chaînes TV</strong>, <strong className="text-foreground">+100 000 films</strong>, 
            <strong className="text-foreground"> +25 000 séries</strong> et tous les <strong className="text-foreground">matchs de foot en direct</strong> (Ligue 1, Champions League) en qualité 4K HDR.
            <strong className="text-primary"> Test gratuit 24h</strong> sans engagement.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/#pricing">
              <Button size="lg" className="text-lg px-8 py-6 btn-primary text-primary-foreground">
                <Play className="w-5 h-5 mr-2" />
                Tester Gratuitement 24h
              </Button>
            </Link>
            <Link to="/nos-offres">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10">
                Voir les Offres dès 25€
              </Button>
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>+15 000 chaînes TV</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>Qualité 4K HDR</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>Support 7j/7</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-secondary" />
              <span>Sans engagement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Ce que vous obtenez */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Tout ce que vous obtenez avec <span className="gradient-text">IPTV France</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card-premium p-6 rounded-2xl text-center">
              <Tv className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">+15 000 Chaînes</h3>
              <p className="text-muted-foreground">TF1, France 2, M6, Canal+, beIN Sports, RMC Sport et toutes les chaînes du monde</p>
            </div>
            <div className="card-premium p-6 rounded-2xl text-center">
              <Film className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">+100 000 Films</h3>
              <p className="text-muted-foreground">Catalogue Netflix, Disney+, Prime Video, films récents et classiques en VF/VOSTFR</p>
            </div>
            <div className="card-premium p-6 rounded-2xl text-center">
              <Trophy className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">+25 000 Séries</h3>
              <p className="text-muted-foreground">Toutes les séries populaires, nouveautés chaque semaine, VF et VOSTFR disponibles</p>
            </div>
            <div className="card-premium p-6 rounded-2xl text-center">
              <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Sport en Direct</h3>
              <p className="text-muted-foreground">Ligue 1, Champions League, Premier League, UFC, F1, Tennis en 4K</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Pourquoi choisir <span className="gradient-text">RoiDuStreaming</span> pour l'IPTV France ?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Serveurs Premium</h3>
                <p className="text-muted-foreground">Nos serveurs sont optimisés pour une latence minimale et une stabilité maximale, même pendant les grands événements sportifs.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Activation Immédiate</h3>
                <p className="text-muted-foreground">Recevez vos identifiants par email en moins de 5 minutes après votre commande ou demande de test gratuit.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Support 7j/7</h3>
                <p className="text-muted-foreground">Équipe de support disponible sur Telegram et email pour vous aider à tout moment.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Mise à Jour Auto</h3>
                <p className="text-muted-foreground">Le contenu est mis à jour automatiquement. Nouveaux films et séries ajoutés chaque semaine.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparatif Prix */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Économisez avec l'<span className="gradient-text">IPTV France</span>
          </h2>

          <div className="card-premium p-8 rounded-2xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-2">Service</th>
                    <th className="text-center py-4 px-2">Prix/mois</th>
                    <th className="text-center py-4 px-2">Contenu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="py-4 px-2">Netflix + Canal+ + beIN</td>
                    <td className="text-center py-4 px-2 text-destructive font-bold">~70€/mois</td>
                    <td className="text-center py-4 px-2">Limité</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-4 px-2">Netflix + Disney+ + Prime</td>
                    <td className="text-center py-4 px-2 text-destructive font-bold">~40€/mois</td>
                    <td className="text-center py-4 px-2">Films/Séries</td>
                  </tr>
                  <tr className="bg-primary/10">
                    <td className="py-4 px-2 font-bold text-primary">RoiDuStreaming IPTV</td>
                    <td className="text-center py-4 px-2 text-secondary font-bold">5€/mois</td>
                    <td className="text-center py-4 px-2 font-bold">TOUT INCLUS</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-center text-muted-foreground mt-6">
              💰 Économisez jusqu'à <strong className="text-primary">780€/an</strong> avec notre abonnement annuel à 60€
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Questions Fréquentes sur l'<span className="gradient-text">IPTV France</span>
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="card-premium border-none rounded-xl overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline text-left font-semibold hover:bg-primary/5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à découvrir le meilleur <span className="gradient-text">IPTV France</span> ?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Rejoignez plus de 10 000 clients satisfaits et profitez de tout le streaming français.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#pricing">
              <Button size="lg" className="text-lg px-8 py-6 btn-primary text-primary-foreground">
                <Play className="w-5 h-5 mr-2" />
                Test Gratuit 24h
              </Button>
            </Link>
            <Link to="/nos-offres">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10">
                Voir les Offres
              </Button>
            </Link>
          </div>

          {/* Liens internes SEO */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground mb-4">Découvrez aussi :</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/abonnement-iptv" className="text-primary hover:underline">Abonnement IPTV</Link>
              <Link to="/iptv-pas-cher" className="text-primary hover:underline">IPTV Pas Cher</Link>
              <Link to="/nos-offres" className="text-primary hover:underline">Nos Offres</Link>
              <Link to="/tutoriels" className="text-primary hover:underline">Tutoriels Installation</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IptvFrance;
